import React, { useState, useEffect } from 'react';

const API = '/api';

function authHeaders(user) {
  return { 'Content-Type': 'application/json', Authorization: `Token ${user.token}` };
}

// ==================== Withdrawal panel (generic, reused by 6 apps) ====================

function WithdrawalPanel({ user, appName }) {
  const [items, setItems] = useState(null);

  const load = () => {
    fetch(`${API}/admin-actions/withdrawals/${appName}/`, { headers: authHeaders(user) })
      .then((r) => r.json()).then((d) => setItems(d.withdrawals || []));
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const act = async (id, action) => {
    let tx_id = '';
    if (action === 'approve') {
      tx_id = prompt('Optional: paste the Pi transaction ID after sending payment manually (or leave blank):', '') || '';
    } else if (!window.confirm('Reject this withdrawal request?')) {
      return;
    }
    await fetch(`${API}/admin-actions/withdrawals/${appName}/${id}/`, {
      method: 'POST', headers: authHeaders(user), body: JSON.stringify({ action, tx_id }),
    });
    load();
  };

  if (items === null) return <p style={{ fontSize: 12, color: '#999' }}>Loading...</p>;
  if (items.length === 0) return <p style={{ fontSize: 12, color: '#999' }}>No pending withdrawals.</p>;

  return (
    <div>
      {items.map((w) => (
        <div key={w.id} style={{ background: '#F7F5F0', borderRadius: 8, padding: '0.6rem 0.75rem', marginBottom: 6 }}>
          <p style={{ fontSize: 13, fontWeight: 500, margin: '0 0 2px' }}>@{w.recipient} - {w.amount} Pi</p>
          <p style={{ fontSize: 11, color: '#5F5E5A', margin: '0 0 6px', wordBreak: 'break-all' }}>{w.pi_wallet_address}</p>
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={() => act(w.id, 'approve')} style={{ fontSize: 11, padding: '4px 10px', border: 'none', borderRadius: 6, background: '#0F6E56', color: '#fff', cursor: 'pointer' }}>✅ Approve (paid manually)</button>
            <button onClick={() => act(w.id, 'reject')} style={{ fontSize: 11, padding: '4px 10px', border: '1px solid #E3E1D9', borderRadius: 6, background: '#fff', cursor: 'pointer' }}>✕ Reject</button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ==================== Merchant approval panel ====================

function MerchantPanel({ user }) {
  const [items, setItems] = useState(null);
  const load = () => fetch(`${API}/admin-actions/merchants/`, { headers: authHeaders(user) }).then((r) => r.json()).then((d) => setItems(d.items || []));
  useEffect(() => { load(); }, []); // eslint-disable-line

  const act = async (id, action) => {
    await fetch(`${API}/admin-actions/merchants/${id}/`, { method: 'POST', headers: authHeaders(user), body: JSON.stringify({ action }) });
    load();
  };

  if (items === null) return <p style={{ fontSize: 12, color: '#999' }}>Loading...</p>;
  if (items.length === 0) return <p style={{ fontSize: 12, color: '#999' }}>No pending merchant approvals.</p>;

  return items.map((m) => (
    <div key={m.id} style={{ background: '#F7F5F0', borderRadius: 8, padding: '0.6rem 0.75rem', marginBottom: 6 }}>
      <p style={{ fontSize: 13, fontWeight: 500, margin: '0 0 2px' }}>{m.business_name} (@{m.username})</p>
      <p style={{ fontSize: 11, color: '#5F5E5A', margin: '0 0 6px' }}>{m.category}</p>
      <p style={{ fontSize: 11, color: '#5F5E5A', margin: '0 0 4px' }}>{[m.address, m.city, m.country].filter(Boolean).join(', ')}</p>
      <p style={{ fontSize: 11, color: '#5F5E5A', margin: '0 0 4px' }}>{[m.phone, m.email, m.pi_username ? 'Pi: ' + m.pi_username : ''].filter(Boolean).join(' · ')}</p>
      {m.tagline ? <p style={{ fontSize: 11, fontStyle: 'italic', color: '#5F5E5A', margin: '0 0 4px' }}>{m.tagline}</p> : null}
      {m.description ? <p style={{ fontSize: 11, color: '#444441', margin: '0 0 6px', whiteSpace: 'pre-wrap' }}>{m.description}</p> : null}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, margin: '0 0 8px' }}>
        {[['Website', m.website], ['Facebook', m.facebook], ['Instagram', m.instagram], ['LinkedIn', m.linkedin], ['YouTube', m.youtube]].filter(function(x){ return x[1]; }).map(function(x){
          var u = x[1].indexOf('http') === 0 ? x[1] : 'https://' + x[1];
          return <a key={x[0]} href={u} target="_blank" rel="noreferrer" style={{ fontSize: 11, padding: '3px 9px', border: '1px solid #E3E1D9', borderRadius: 6, background: '#fff', color: '#0F6E56', textDecoration: 'none' }}>{x[0]} ↗</a>;
        })}
        {![m.website, m.facebook, m.instagram, m.linkedin, m.youtube].some(Boolean) ? <span style={{ fontSize: 11, color: '#999' }}>No social links provided</span> : null}
      </div>
      <div style={{ display: 'flex', gap: 6 }}>
        <button onClick={() => act(m.id, 'approve')} style={{ fontSize: 11, padding: '4px 10px', border: 'none', borderRadius: 6, background: '#0F6E56', color: '#fff', cursor: 'pointer' }}>✅ Approve</button>
        <button onClick={() => act(m.id, 'reject')} style={{ fontSize: 11, padding: '4px 10px', border: '1px solid #E3E1D9', borderRadius: 6, background: '#fff', cursor: 'pointer' }}>✕ Reject</button>
      </div>
    </div>
  ));
}

// ==================== Task Center panel ====================

function VideosPanel({ user }) {
  const [apps, setApps] = useState(null);
  const [filter, setFilter] = useState('pending');

  const load = () => fetch('/api/videos/admin/applications/', { headers: authHeaders(user) })
    .then((r) => r.json()).then((d) => setApps(d.applications || []));
  useEffect(() => { load(); }, []); // eslint-disable-line

  const act = async (id, action) => {
    await fetch(`/api/videos/admin/applications/${id}/${action}/`, { method: 'POST', headers: authHeaders(user) });
    load();
  };

  const shown = (apps || []).filter((a) => a.status === filter);
  const tabS = (on) => ({ padding: '4px 12px', fontSize: 11, fontWeight: 600, border: `1px solid ${on ? '#0F6E56' : '#E3E1D9'}`, borderRadius: 14, background: on ? '#E1F5EE' : '#fff', color: on ? '#0F6E56' : '#5F5E5A', cursor: 'pointer', marginRight: 4 });

  return (
    <div>
      <div style={{ display: 'flex', marginBottom: 8 }}>
        {['pending', 'approved', 'rejected'].map((s) => (
          <button key={s} style={tabS(filter === s)} onClick={() => setFilter(s)}>
            {s} ({(apps || []).filter((a) => a.status === s).length})
          </button>
        ))}
      </div>
      {apps === null && <p style={{ fontSize: 12, color: '#999' }}>Loading...</p>}
      {apps && shown.length === 0 && <p style={{ fontSize: 12, color: '#999' }}>No {filter} applications.</p>}
      {shown.map((a) => (
        <div key={a.id} style={{ background: '#F7F5F0', borderRadius: 8, padding: '0.6rem 0.75rem', marginBottom: 6 }}>
          <p style={{ fontSize: 12, margin: '0 0 2px' }}><b>{a.channel_name}</b> by @{a.username} \u00b7 {a.category}</p>
          {a.description && <p style={{ fontSize: 11, color: '#5F5E5A', margin: '0 0 4px' }}>{a.description}</p>}
          <p style={{ fontSize: 10, color: '#999', margin: '0 0 6px' }}>{a.created_at}</p>
          {a.status === 'pending' && (
            <div style={{ display: 'flex', gap: 6 }}>
              <button onClick={() => act(a.id, 'approve')} style={{ fontSize: 11, padding: '4px 10px', border: 'none', borderRadius: 6, background: '#0F6E56', color: '#fff', cursor: 'pointer' }}>Approve</button>
              <button onClick={() => act(a.id, 'reject')} style={{ fontSize: 11, padding: '4px 10px', border: '1px solid #E3E1D9', borderRadius: 6, background: '#fff', cursor: 'pointer' }}>Reject</button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}


// ==================== Escrow / disputes ====================
function EscrowPanel({ user }) {
  const [tab, setTab] = React.useState('disputed');
  const [data, setData] = React.useState(null);
  const [busy, setBusy] = React.useState({});

  const loadEscrow = React.useCallback(async () => {
    try {
      const r = await fetch(`/api/merchants/admin/escrow/?status=${tab}`, { headers: authHeaders() });
      setData(await r.json());
    } catch (e) { setData({ orders: [], totals: {} }); }
  }, [tab]);
  React.useEffect(() => { loadEscrow(); }, [loadEscrow]);

  const resolve = async (id, action) => {
    const w = action === 'refund' ? 'refund the buyer' : 'release to the merchant';
    if (!window.confirm(`Are you sure you want to ${w}?`)) return;
    const note = window.prompt('Note (optional)') || '';
    setBusy((b) => ({ ...b, [id]: true }));
    try {
      const r = await fetch(`/api/merchants/admin/escrow/${id}/resolve/`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify({ action, note }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Failed');
      loadEscrow();
    } catch (e) { alert(e.message); }
    setBusy((b) => ({ ...b, [id]: false }));
  };

  const T = (data && data.totals) || {};
  const box = { flex: 1, minWidth: 95, background: '#F4F3EE', borderRadius: 8, padding: '8px 10px' };
  const tb = (k, label) => (
    <button key={k} onClick={() => setTab(k)} style={{ fontSize: 12, padding: '6px 10px', border: 'none', borderBottom: tab === k ? '2px solid #0F6E56' : '2px solid transparent', background: 'none', color: tab === k ? '#0F6E56' : '#5F5E5A', cursor: 'pointer', fontWeight: tab === k ? 600 : 400 }}>{label}</button>
  );

  return (
    <div style={{ padding: '1rem' }}>
      <h3 style={{ fontSize: 16, margin: '0 0 10px' }}>🔒 Escrow</h3>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
        <div style={box}><p style={{ fontSize: 11, color: '#5F5E5A', margin: 0 }}>Held</p><p style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>{T.held_pi || 0} Pi</p></div>
        <div style={box}><p style={{ fontSize: 11, color: '#5F5E5A', margin: 0 }}>Released</p><p style={{ fontSize: 15, fontWeight: 700, margin: 0, color: '#0F6E56' }}>{T.released_pi || 0} Pi</p></div>
        <div style={box}><p style={{ fontSize: 11, color: '#5F5E5A', margin: 0 }}>Refunded</p><p style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>{T.refunded_pi || 0} Pi</p></div>
      </div>
      <div style={{ display: 'flex', gap: 2, borderBottom: '0.5px solid #E3E1D9', marginBottom: 10, flexWrap: 'wrap' }}>
        {tb('disputed', `⚠️ Disputes (${T.disputed_count || 0})`)}
        {tb('held', `🔒 Held (${T.held_count || 0})`)}
        {tb('released', '✅ Released')}
        {tb('refunded', '↩️ Refunded')}
      </div>
      {!data ? <p style={{ fontSize: 13, color: '#5F5E5A' }}>Loading…</p>
        : !data.orders || data.orders.length === 0 ? <p style={{ fontSize: 13, color: '#5F5E5A' }}>Nothing here.</p>
        : data.orders.map((o) => (
          <div key={o.id} style={{ background: '#fff', border: `0.5px solid ${o.escrow_status === 'disputed' ? '#C0392B' : '#E3E1D9'}`, borderRadius: 10, padding: '0.75rem', marginBottom: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <p style={{ fontSize: 13, fontWeight: 600, margin: 0 }}>{o.tracking_code}</p>
              <p style={{ fontSize: 13, fontWeight: 600, margin: 0 }}>{o.total_pi} Pi</p>
            </div>
            <p style={{ fontSize: 12, color: '#5F5E5A', margin: '2px 0' }}>@{o.buyer} → {o.merchant} · {o.created_at}</p>
            <p style={{ fontSize: 12, margin: '2px 0' }}>{(o.items || []).map((i) => `${i.quantity}x ${i.name}`).join(', ')}</p>
            {o.escrow_note && <p style={{ fontSize: 11, color: '#C0392B', margin: '4px 0' }}>{o.escrow_note}</p>}
            {(o.escrow_status === 'disputed' || o.escrow_status === 'held') && (
              <div style={{ marginTop: 8 }}>
                <button disabled={busy[o.id]} onClick={() => resolve(o.id, 'release')} style={{ fontSize: 12, padding: '6px 12px', border: 'none', borderRadius: 8, background: '#0F6E56', color: '#fff', cursor: 'pointer', marginRight: 6 }}>✅ Release to merchant</button>
                <button disabled={busy[o.id]} onClick={() => resolve(o.id, 'refund')} style={{ fontSize: 12, padding: '6px 12px', border: '0.5px solid #C0392B', borderRadius: 8, background: '#fff', color: '#C0392B', cursor: 'pointer' }}>↩️ Refund buyer</button>
              </div>
            )}
          </div>
        ))}
    </div>
  );
}

function HealthPanel({ user }) {
  const [docs, setDocs] = useState(null);
  const [plans, setPlans] = useState([]);
  const [np, setNp] = useState({ name: '', price_pi: '', duration_days: '30', checkups: '0', description: '' });
  const [facs, setFacs] = useState([]);

  const load = () => {
    fetch('/api/health/admin/pending-doctors/', { headers: authHeaders(user) }).then((r) => r.json()).then((d) => setDocs(d.items || []));
    fetch('/api/health/admin/plans/', { headers: authHeaders(user) }).then((r) => r.json()).then((d) => setPlans(d.items || []));
    fetch('/api/health/admin/pending-facilities/', { headers: authHeaders(user) }).then((r) => r.json()).then((d) => setFacs(d.items || []));
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const docAction = async (id, action) => {
    await fetch(`/api/health/admin/doctor/${id}/action/`, { method: 'POST', headers: authHeaders(user), body: JSON.stringify({ action }) });
    load();
  };
  const addPlan = async () => {
    await fetch('/api/health/admin/plans/', { method: 'POST', headers: authHeaders(user), body: JSON.stringify(np) });
    setNp({ name: '', price_pi: '', duration_days: '30', checkups: '0', description: '' }); load();
  };
  const facAction = async (id, action) => {
    await fetch(`/api/health/admin/facility/${id}/action/`, { method: 'POST', headers: authHeaders(user), body: JSON.stringify({ action }) });
    load();
  };
  const togglePlan = async (id) => {
    await fetch(`/api/health/admin/plan/${id}/toggle/`, { method: 'POST', headers: authHeaders(user) });
    load();
  };

  const inp = { padding: '6px 9px', fontSize: 12, border: '0.5px solid #E3E1D9', borderRadius: 6, marginRight: 4, marginBottom: 4 };
  return (
    <div>
      <p style={{ fontSize: 12, fontWeight: 600, margin: '0 0 6px' }}>🩺 Doctors to approve</p>
      {docs === null && <p style={{ fontSize: 12, color: '#999' }}>Loading...</p>}
      {docs && docs.length === 0 && <p style={{ fontSize: 12, color: '#999' }}>No pending doctors.</p>}
      {docs && docs.map((d) => (
        <div key={d.id} style={{ background: '#F7F5F0', borderRadius: 8, padding: '0.6rem 0.75rem', marginBottom: 6 }}>
          <p style={{ fontSize: 12, margin: '0 0 2px' }}><b>Dr. {d.name}</b> (@{d.username}) · {d.department}</p>
          <p style={{ fontSize: 11, color: '#5F5E5A', margin: '0 0 6px' }}>{d.qualifications} · {d.clinic}{d.city ? `, ${d.city}` : ''}</p>
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={() => docAction(d.id, 'approve')} style={{ fontSize: 11, padding: '4px 10px', border: 'none', borderRadius: 6, background: '#0F6E56', color: '#fff', cursor: 'pointer' }}>✅ Approve</button>
            <button onClick={() => docAction(d.id, 'reject')} style={{ fontSize: 11, padding: '4px 10px', border: '1px solid #E3E1D9', borderRadius: 6, background: '#fff', cursor: 'pointer' }}>✕ Reject</button>
          </div>
        </div>
      ))}

      {facs.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <p style={{ fontSize: 12, fontWeight: 600, margin: '0 0 6px' }}>🏥 Facilities to approve</p>
          {facs.map((f) => (
            <div key={f.id} style={{ background: '#F7F5F0', borderRadius: 8, padding: '0.5rem 0.7rem', marginBottom: 5 }}>
              <p style={{ fontSize: 12, margin: '0 0 4px' }}><b>{f.name}</b> ({f.type}) · {f.city} · {f.contact}</p>
              <button onClick={() => facAction(f.id, 'approve')} style={{ fontSize: 11, padding: '3px 10px', border: 'none', borderRadius: 6, background: '#0F6E56', color: '#fff', cursor: 'pointer', marginRight: 4 }}>✅ Approve</button>
              <button onClick={() => facAction(f.id, 'reject')} style={{ fontSize: 11, padding: '3px 10px', border: '1px solid #E3E1D9', borderRadius: 6, background: '#fff', cursor: 'pointer' }}>✕ Reject</button>
            </div>
          ))}
        </div>
      )}
      <div style={{ borderTop: '1px solid #E3E1D9', marginTop: 14, paddingTop: 10 }}>
        <p style={{ fontSize: 14, fontWeight: 700, margin: '0 0 8px' }}>💳 Manage Subscription Plans</p>
      </div>
      {plans.map((p) => (
        <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, padding: '4px 0', borderBottom: '1px solid #eee' }}>
          <span>{p.name} — {p.price_pi}π / {p.duration_days}d / {p.checkups === 0 ? '∞' : p.checkups} checkups {!p.is_active && '(off)'}</span>
          <button onClick={() => togglePlan(p.id)} style={{ fontSize: 10, padding: '2px 8px', border: '1px solid #E3E1D9', borderRadius: 5, background: '#fff', cursor: 'pointer' }}>{p.is_active ? 'Disable' : 'Enable'}</button>
        </div>
      ))}
      <div style={{ marginTop: 8 }}>
        <input style={inp} placeholder="Plan name" value={np.name} onChange={(e) => setNp({ ...np, name: e.target.value })} />
        <input style={{ ...inp, width: 60 }} placeholder="Pi" value={np.price_pi} onChange={(e) => setNp({ ...np, price_pi: e.target.value })} />
        <input style={{ ...inp, width: 50 }} placeholder="days" value={np.duration_days} onChange={(e) => setNp({ ...np, duration_days: e.target.value })} />
        <input style={{ ...inp, width: 70 }} placeholder="checkups(0=∞)" value={np.checkups} onChange={(e) => setNp({ ...np, checkups: e.target.value })} />
        <button onClick={addPlan} style={{ fontSize: 11, padding: '5px 12px', border: 'none', borderRadius: 6, background: '#0F6E56', color: '#fff', cursor: 'pointer' }}>+ Add plan</button>
      </div>
    </div>
  );
}

function TaskCenterPanel({ user }) {
  const [items, setItems] = useState(null);
  const load = () => fetch('/api/taskcenter/admin/disputes/', { headers: authHeaders(user) }).then((r) => r.json()).then((d) => setItems(d.items || []));
  useEffect(() => { load(); }, []); // eslint-disable-line

  const act = async (id, action) => {
    await fetch(`/api/taskcenter/admin/disputes/${id}/resolve/`, { method: 'POST', headers: authHeaders(user), body: JSON.stringify({ action }) });
    load();
  };

  return (
    <div>
      <p style={{ fontSize: 12, fontWeight: 600, margin: '0 0 6px' }}>⚖️ Disputes</p>
      {items === null && <p style={{ fontSize: 12, color: '#999' }}>Loading...</p>}
      {items && items.length === 0 && <p style={{ fontSize: 12, color: '#999' }}>No open disputes.</p>}
      {items && items.map((d) => (
        <div key={d.id} style={{ background: '#F7F5F0', borderRadius: 8, padding: '0.6rem 0.75rem', marginBottom: 6 }}>
          <p style={{ fontSize: 12, margin: '0 0 2px' }}><b>{d.task_title}</b> — @{d.worker} vs @{d.creator} · {d.reward} π</p>
          <p style={{ fontSize: 11, color: '#5F5E5A', margin: '0 0 6px' }}>Creator: {d.reject_reason} | Worker: {d.dispute_note}</p>
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={() => act(d.id, 'approve')} style={{ fontSize: 11, padding: '4px 10px', border: 'none', borderRadius: 6, background: '#0F6E56', color: '#fff', cursor: 'pointer' }}>✅ Worker sahi — pay</button>
            <button onClick={() => act(d.id, 'reject')} style={{ fontSize: 11, padding: '4px 10px', border: '1px solid #E3E1D9', borderRadius: 6, background: '#fff', cursor: 'pointer' }}>✕ Creator sahi</button>
          </div>
        </div>
      ))}
      <p style={{ fontSize: 12, fontWeight: 600, margin: '10px 0 6px' }}>💸 Withdrawals</p>
      <WithdrawalPanel user={user} appName="taskcenter" />
      <p style={{ fontSize: 11, color: '#5F5E5A', marginTop: 8 }}>⚙️ Price settings: /tasks page → Admin tab</p>
    </div>
  );
}

// ==================== Realty agent approval panel ====================

function AgentPanel({ user }) {
  const [items, setItems] = useState(null);
  const load = () => fetch(`${API}/admin-actions/agents/`, { headers: authHeaders(user) }).then((r) => r.json()).then((d) => setItems(d.items || []));
  useEffect(() => { load(); }, []); // eslint-disable-line

  const act = async (id, action) => {
    await fetch(`${API}/admin-actions/agents/${id}/`, { method: 'POST', headers: authHeaders(user), body: JSON.stringify({ action }) });
    load();
  };

  if (items === null) return <p style={{ fontSize: 12, color: '#999' }}>Loading...</p>;
  if (items.length === 0) return <p style={{ fontSize: 12, color: '#999' }}>No pending agent approvals.</p>;

  return items.map((a) => (
    <div key={a.id} style={{ background: '#F7F5F0', borderRadius: 8, padding: '0.6rem 0.75rem', marginBottom: 6 }}>
      <p style={{ fontSize: 13, fontWeight: 500, margin: '0 0 2px' }}>@{a.username} - {a.role}{a.company_name ? ` (${a.company_name})` : ''}</p>
      <p style={{ fontSize: 11, color: '#5F5E5A', margin: '0 0 6px' }}>{a.phone}</p>
      <div style={{ display: 'flex', gap: 6 }}>
        <button onClick={() => act(a.id, 'approve')} style={{ fontSize: 11, padding: '4px 10px', border: 'none', borderRadius: 6, background: '#0F6E56', color: '#fff', cursor: 'pointer' }}>✅ Approve</button>
        <button onClick={() => act(a.id, 'reject')} style={{ fontSize: 11, padding: '4px 10px', border: '1px solid #E3E1D9', borderRadius: 6, background: '#fff', cursor: 'pointer' }}>✕ Reject</button>
      </div>
    </div>
  ));
}

// ==================== Freelance disputes panel ====================

function DisputePanel({ user }) {
  const [items, setItems] = useState(null);
  const load = () => fetch(`${API}/admin-actions/disputes/`, { headers: authHeaders(user) }).then((r) => r.json()).then((d) => setItems(d.items || []));
  useEffect(() => { load(); }, []); // eslint-disable-line

  const act = async (id, resolution) => {
    if (!window.confirm(`Resolve in favor of ${resolution === 'favor_seller' ? 'seller' : 'buyer'}?`)) return;
    await fetch(`${API}/admin-actions/disputes/${id}/`, { method: 'POST', headers: authHeaders(user), body: JSON.stringify({ resolution }) });
    load();
  };

  if (items === null) return <p style={{ fontSize: 12, color: '#999' }}>Loading...</p>;
  if (items.length === 0) return <p style={{ fontSize: 12, color: '#999' }}>No disputed orders.</p>;

  return items.map((o) => (
    <div key={o.id} style={{ background: '#F7F5F0', borderRadius: 8, padding: '0.6rem 0.75rem', marginBottom: 6 }}>
      <p style={{ fontSize: 13, fontWeight: 500, margin: '0 0 2px' }}>{o.gig_title} - {o.price_pi} Pi</p>
      <p style={{ fontSize: 11, color: '#5F5E5A', margin: '0 0 6px' }}>Buyer: @{o.buyer} · Seller: @{o.seller}</p>
      <div style={{ display: 'flex', gap: 6 }}>
        <button onClick={() => act(o.id, 'favor_seller')} style={{ fontSize: 11, padding: '4px 10px', border: 'none', borderRadius: 6, background: '#0F6E56', color: '#fff', cursor: 'pointer' }}>Favor Seller</button>
        <button onClick={() => act(o.id, 'favor_buyer')} style={{ fontSize: 11, padding: '4px 10px', border: '1px solid #E3E1D9', borderRadius: 6, background: '#fff', cursor: 'pointer' }}>Favor Buyer</button>
      </div>
    </div>
  ));
}

// ==================== Matrimony verification panel ====================

function MatrimonyPanel({ user }) {
  const [items, setItems] = useState(null);
  const load = () => fetch(`${API}/admin-actions/matrimony-unverified/`, { headers: authHeaders(user) }).then((r) => r.json()).then((d) => setItems(d.items || []));
  useEffect(() => { load(); }, []); // eslint-disable-line

  const verify = async (id) => {
    await fetch(`${API}/admin-actions/matrimony-verify/${id}/`, { method: 'POST', headers: authHeaders(user), body: JSON.stringify({}) });
    load();
  };

  if (items === null) return <p style={{ fontSize: 12, color: '#999' }}>Loading...</p>;
  if (items.length === 0) return <p style={{ fontSize: 12, color: '#999' }}>No unverified profiles.</p>;

  return items.map((p) => (
    <div key={p.id} style={{ background: '#F7F5F0', borderRadius: 8, padding: '0.6rem 0.75rem', marginBottom: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <div>
        <p style={{ fontSize: 13, fontWeight: 500, margin: '0 0 2px' }}>@{p.username}</p>
        <p style={{ fontSize: 11, color: '#5F5E5A', margin: 0 }}>{p.city} · {p.gender}</p>
      </div>
      <button onClick={() => verify(p.id)} style={{ fontSize: 11, padding: '4px 10px', border: 'none', borderRadius: 6, background: '#0F6E56', color: '#fff', cursor: 'pointer' }}>✅ Verify</button>
    </div>
  ));
}

// ==================== PetCare lost reports panel (view only) ====================

function LostReportsPanel({ user }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    fetch(`${API}/admin-actions/lost-reports/`, { headers: authHeaders(user) }).then((r) => r.json()).then((d) => setItems(d.items || []));
  }, []); // eslint-disable-line

  if (items === null) return <p style={{ fontSize: 12, color: '#999' }}>Loading...</p>;
  if (items.length === 0) return <p style={{ fontSize: 12, color: '#999' }}>No active lost-pet reports.</p>;

  return items.map((r) => (
    <div key={r.id} style={{ background: '#F7F5F0', borderRadius: 8, padding: '0.6rem 0.75rem', marginBottom: 6 }}>
      <p style={{ fontSize: 13, fontWeight: 500, margin: 0 }}>{r.pet_name} - {r.city}</p>
      <p style={{ fontSize: 11, color: '#5F5E5A', margin: 0 }}>Reported by @{r.reporter}</p>
    </div>
  ));
}

// ==================== Growth Stats (date-filtered users + revenue) ====================

function GrowthStatsSection({ user }) {
  const [dateFrom, setDateFrom] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = () => {
    setLoading(true);
    const params = dateFrom ? `?date_from=${dateFrom}` : '';
    fetch(`${API}/admin-growth-stats/${params}`, { headers: authHeaders(user) })
      .then((r) => r.json())
      .then((d) => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  return (
    <div style={{ background: '#fff', border: '1px solid #E3E1D9', borderRadius: 12, padding: '1rem', marginBottom: 20 }}>
      <h3 style={{ fontSize: 15, margin: '0 0 10px' }}>📈 Growth Stats</h3>
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, alignItems: 'center', flexWrap: 'wrap' }}>
        <label style={{ fontSize: 12, color: '#5F5E5A' }}>New users & revenue since:</label>
        <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} style={{ padding: '6px 10px', fontSize: 13, border: '1px solid #E3E1D9', borderRadius: 6 }} />
        <button onClick={load} disabled={loading} style={{ padding: '6px 14px', fontSize: 12, border: 'none', borderRadius: 6, background: '#0F6E56', color: '#fff', cursor: 'pointer' }}>
          {loading ? 'Loading...' : dateFrom ? 'Filter' : 'Show All-Time'}
        </button>
        {dateFrom && <button onClick={() => { setDateFrom(''); setTimeout(load, 0); }} style={{ padding: '6px 14px', fontSize: 12, border: '1px solid #E3E1D9', borderRadius: 6, background: '#fff', cursor: 'pointer' }}>Clear</button>}
      </div>

      {data && (
        <>
          <div style={{ display: 'flex', gap: 12, marginBottom: 14 }}>
            <div style={{ flex: 1, textAlign: 'center', background: '#E1F5EE', borderRadius: 10, padding: '0.75rem' }}>
              <p style={{ fontSize: 11, color: '#5F5E5A', margin: '0 0 2px' }}>New Users {data.date_from ? `since ${data.date_from}` : '(all-time)'}</p>
              <p style={{ fontSize: 20, fontWeight: 700, color: '#0F6E56', margin: 0 }}>{data.totals.total_new_users}</p>
            </div>
            <div style={{ flex: 1, textAlign: 'center', background: '#FDF0DD', borderRadius: 10, padding: '0.75rem' }}>
              <p style={{ fontSize: 11, color: '#5F5E5A', margin: '0 0 2px' }}>Revenue {data.date_from ? `since ${data.date_from}` : '(all-time)'}</p>
              <p style={{ fontSize: 20, fontWeight: 700, color: '#91600C', margin: 0 }}>{data.totals.total_revenue_pi} Pi</p>
            </div>
          </div>

          {data.stats.map((s) => (
            <div key={s.name} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #F1EFE8' }}>
              <span style={{ fontSize: 13 }}>{s.icon} {s.name}</span>
              {s.error ? (
                <span style={{ fontSize: 11, color: '#dc2626' }}>⚠️ error</span>
              ) : (
                <span style={{ fontSize: 12, color: '#5F5E5A' }}>
                  {s.new_users} users · {s.revenue_pi > 0 ? `${s.revenue_pi} Pi` : (s.revenue_note || '0 Pi')}
                </span>
              )}
            </div>
          ))}
        </>
      )}
    </div>
  );
}



function AppCard({ app, user }) {
  const [expanded, setExpanded] = useState(false);
  const cardStyle = { background: '#fff', border: '1px solid #E3E1D9', borderRadius: 12, padding: '1rem', marginBottom: 12 };

  const panelFor = () => {
    if (app.panel === 'merchant') return <MerchantPanel user={user} />;
    if (app.panel === 'taskcenter') return <TaskCenterPanel user={user} />;
    if (app.panel === 'health') return <HealthPanel user={user} />;
    if (app.panel === 'escrow') return <EscrowPanel user={user} />;
    if (app.panel === 'videos') return <VideosPanel user={user} />;
    if (app.panel === 'agent') return <AgentPanel user={user} />;
    if (app.panel === 'dispute') return <DisputePanel user={user} />;
    if (app.panel === 'matrimony') return <MatrimonyPanel user={user} />;
    if (app.panel === 'lostreports') return <LostReportsPanel user={user} />;
    if (app.panel === 'careers-link') return <p style={{ fontSize: 13 }}>This app has its own admin tab - <a href="/careers" style={{ color: '#0F6E56', fontWeight: 600 }}>Open Careers Admin →</a></p>;
    if (app.panel && app.panel.startsWith('withdrawal:')) return <WithdrawalPanel user={user} appName={app.panel.split(':')[1]} />;
    return <p style={{ fontSize: 12, color: '#999' }}>Nothing actionable for this app.</p>;
  };

  return (
    <div style={cardStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <h3 style={{ fontSize: 15, margin: 0 }}>{app.icon} {app.name}</h3>
        {app.panel && (
          <button onClick={() => setExpanded(!expanded)} style={{ fontSize: 12, color: '#0F6E56', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>
            {expanded ? 'Hide ▲' : 'View & Manage ▼'}
          </button>
        )}
      </div>

      {app.error ? (
        <p style={{ fontSize: 12, color: '#dc2626', margin: 0 }}>⚠️ Could not load: {app.error}</p>
      ) : (
        <>
          {app.pending_action > 0 && (
            <p style={{ fontSize: 13, color: '#A32D2D', fontWeight: 600, margin: '0 0 4px' }}>
              🔴 {app.pending_action} {app.pending_label} need attention
            </p>
          )}
          {app.pending_action === 0 && app.pending_label && (
            <p style={{ fontSize: 12, color: '#5F5E5A', margin: '0 0 4px' }}>✅ No {app.pending_label} pending</p>
          )}
          {app.commission_pi > 0 && <p style={{ fontSize: 12, color: '#5F5E5A', margin: '0 0 2px' }}>💰 {app.commission_pi} Pi commission earned</p>}
          {app.pending_withdrawals_pi > 0 && <p style={{ fontSize: 12, color: '#91600C', margin: '0 0 2px' }}>⏳ {app.pending_withdrawals_pi} Pi in pending withdrawals</p>}
          {app.commission_note && <p style={{ fontSize: 12, color: '#5F5E5A', margin: '0 0 2px' }}>{app.commission_note}</p>}
          {app.note && <p style={{ fontSize: 12, color: '#5F5E5A', margin: 0 }}>{app.note}</p>}
        </>
      )}

      {expanded && <div style={{ marginTop: 10, borderTop: '1px solid #E3E1D9', paddingTop: 10 }}>{panelFor()}</div>}
    </div>
  );
}

// Maps each app name (from the backend summary) to which action panel it uses
const PANEL_MAP = {
  'Careers': 'careers-link',
  'Marketplace': 'merchant',
  'Tasks': 'taskcenter',
  'Realty': 'agent',
  'Freelance': 'dispute',
  'Travel': 'withdrawal:travel',
  'Meetup': 'withdrawal:meetup',
  'Academy': 'withdrawal:academy',
  'Matrimony': 'matrimony',
  'NFT': 'withdrawal:nft',
  'PetCare': 'lostreports',
};

function SystemResourcesSection({ user }) {
  const [res, setRes] = useState(null);
  const [drill, setDrill] = useState(null);
  const [drillData, setDrillData] = useState(null);

  const openDrill = (kind) => {
    if (drill === kind) { setDrill(null); return; }
    setDrill(kind); setDrillData(null);
    fetch(`${API}/system-resources/breakdown/?kind=${kind}`, { headers: authHeaders(user) })
      .then((r) => r.json())
      .then((d) => setDrillData(d.items || []))
      .catch(() => setDrillData([]));
  };
  useEffect(() => {
    if (!user) return;
    fetch(`${API}/system-resources/`, { headers: authHeaders(user) })
      .then((r) => r.json())
      .then((d) => { if (!d.error) setRes(d); })
      .catch(() => {});
  }, [user]);

  if (!res) return null;

  const bar = (percent, color) => (
    <div style={{ background: '#F1EFE8', borderRadius: 6, height: 8, overflow: 'hidden', marginTop: 4 }}>
      <div style={{ width: `${percent}%`, background: color, height: '100%' }} />
    </div>
  );

  return (
    <div style={{ background: '#fff', border: '1px solid #E3E1D9', borderRadius: 12, padding: '1rem', marginBottom: 20 }}>
      <h3 style={{ fontSize: 15, marginBottom: 12 }}>🖥️ Server Resources</h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 16 }}>
        <div onClick={() => openDrill('ram')} style={{ cursor: 'pointer' }}>
          <p style={{ fontSize: 12, color: '#5F5E5A', margin: 0 }}>RAM <span style={{ fontSize: 10, color: '#0F6E56' }}>{drill === 'ram' ? '▲' : '▼ tap'}</span></p>
          <p style={{ fontSize: 14, fontWeight: 600, margin: '2px 0' }}>{res.ram.used_gb} / {res.ram.total_gb} GB</p>
          {bar(res.ram.percent, res.ram.percent > 85 ? '#dc2626' : '#0F6E56')}
        </div>
        <div onClick={() => openDrill('disk')} style={{ cursor: 'pointer' }}>
          <p style={{ fontSize: 12, color: '#5F5E5A', margin: 0 }}>Disk <span style={{ fontSize: 10, color: '#0F6E56' }}>{drill === 'disk' ? '▲' : '▼ tap'}</span></p>
          <p style={{ fontSize: 14, fontWeight: 600, margin: '2px 0' }}>{res.disk.used_gb} / {res.disk.total_gb} GB</p>
          {bar(res.disk.percent, res.disk.percent > 85 ? '#dc2626' : '#0F6E56')}
        </div>
        <div onClick={() => openDrill('cpu')} style={{ cursor: 'pointer' }}>
          <p style={{ fontSize: 12, color: '#5F5E5A', margin: 0 }}>CPU <span style={{ fontSize: 10, color: '#0F6E56' }}>{drill === 'cpu' ? '▲' : '▼ tap'}</span></p>
          <p style={{ fontSize: 14, fontWeight: 600, margin: '2px 0' }}>{res.cpu_percent}%</p>
          {bar(res.cpu_percent, res.cpu_percent > 85 ? '#dc2626' : '#0F6E56')}
        </div>
        <div>
          <p style={{ fontSize: 12, color: '#5F5E5A', margin: 0 }}>Bandwidth (since reboot)</p>
          <p style={{ fontSize: 14, fontWeight: 600, margin: '2px 0' }}>↑{res.bandwidth.sent_gb} / ↓{res.bandwidth.received_gb} GB</p>
        </div>
      </div>
      {drill && (
        <div style={{ marginTop: 14, borderTop: '1px solid #E3E1D9', paddingTop: 12 }}>
          <p style={{ fontSize: 13, fontWeight: 600, margin: '0 0 8px' }}>
            {drill === 'ram' ? '🧠 RAM by service' : drill === 'cpu' ? '⚡ CPU by service' : '💾 Disk by folder'}
          </p>
          {drillData === null && <p style={{ fontSize: 12, color: '#999' }}>Scanning…</p>}
          {drillData && drillData.length === 0 && <p style={{ fontSize: 12, color: '#999' }}>No data.</p>}
          {drillData && drillData.map((it, i) => {
            const val = drill === 'ram' ? `${it.ram_mb} MB` : drill === 'cpu' ? `${it.cpu_percent}%` : (it.size_gb != null ? `${it.size_gb} GB` : (it.note || '—'));
            const max = drillData[0] && (drill === 'ram' ? drillData[0].ram_mb : drill === 'cpu' ? drillData[0].cpu_percent : drillData[0].size_gb) || 1;
            const cur = drill === 'ram' ? it.ram_mb : drill === 'cpu' ? it.cpu_percent : (it.size_gb || 0);
            return (
              <div key={i} style={{ marginBottom: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                  <span>{it.label}{it.processes ? ` (${it.processes})` : ''}</span>
                  <b style={{ color: '#0F6E56' }}>{val}</b>
                </div>
                <div style={{ background: '#F1EFE8', borderRadius: 5, height: 6, overflow: 'hidden', marginTop: 3 }}>
                  <div style={{ width: `${Math.min((cur / (max || 1)) * 100, 100)}%`, background: '#0F6E56', height: '100%' }} />
                </div>
              </div>
            );
          })}
          {drill === 'disk' && <p style={{ fontSize: 10, color: '#999', marginTop: 6 }}>Note: bade folders scan hone mein waqt lagta hai; timeout par "scan timed out" aata hai.</p>}
        </div>
      )}
    </div>
  );
}

export default function AdminDashboard({ user }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!user) return;
    fetch(`${API}/admin-dashboard/`, { headers: authHeaders(user) })
      .then((r) => r.json())
      .then((d) => { if (d.error) setError(d.error); else setData(d); })
      .catch(() => setError('Could not load dashboard.'));
  }, [user]);

  if (!user) return <div style={{ padding: 20, textAlign: 'center' }}><h2>🔑 Login Required</h2></div>;
  if (user.username !== 'khalidxpert') return <div style={{ padding: 20, textAlign: 'center' }}><h2>⛔ Admins only</h2></div>;
  if (error) return <div style={{ padding: 20, textAlign: 'center', color: '#dc2626' }}>{error}</div>;
  if (!data) return <div style={{ padding: 20, textAlign: 'center', color: '#999' }}>Loading dashboard...</div>;

  return (
    <div style={{ maxWidth: 700, margin: '0 auto', padding: '1.5rem', fontFamily: 'sans-serif' }}>
      <h1 style={{ fontSize: 22, marginBottom: 4 }}>⚙️ Unified Admin Dashboard</h1>
      <p style={{ fontSize: 13, color: '#5F5E5A', marginBottom: 16 }}>
        Live stats and pending actions across every app - approve, reject, and manage right here, no external admin needed.
      </p>

      <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
        <div style={{ flex: 1, textAlign: 'center', background: '#E1F5EE', borderRadius: 12, padding: '1rem' }}>
          <p style={{ fontSize: 12, color: '#5F5E5A', margin: '0 0 4px' }}>Total Commission Earned</p>
          <p style={{ fontSize: 22, fontWeight: 700, color: '#0F6E56', margin: 0 }}>{data.totals.total_commission_pi} Pi</p>
        </div>
        <div style={{ flex: 1, textAlign: 'center', background: '#FDF0DD', borderRadius: 12, padding: '1rem' }}>
          <p style={{ fontSize: 12, color: '#5F5E5A', margin: '0 0 4px' }}>Pending Withdrawals</p>
          <p style={{ fontSize: 22, fontWeight: 700, color: '#91600C', margin: 0 }}>{data.totals.total_pending_withdrawals_pi} Pi</p>
        </div>
      </div>

      <PlatformEarningsSection user={user} />
      <SystemResourcesSection user={user} />
      <GrowthStatsSection user={user} />

      {data.apps.map((app) => (
        <AppCard key={app.name} app={{ ...app, panel: app.panel || PANEL_MAP[app.name] }} user={user} />
      ))}
    </div>
  );
}


// ==================== PLATFORM EARNINGS (realized + transfer) ====================
function PlatformEarningsSection({ user }) {
  const [d, setD] = React.useState(null);
  const [open, setOpen] = React.useState(false);
  const [busy, setBusy] = React.useState(false);

  const load = React.useCallback(async () => {
    try {
      const r = await fetch(`${API}/platform-earnings/`, { headers: authHeaders(user) });
      setD(await r.json());
    } catch (e) {
      setD({ error: 'Could not load platform earnings.' });
    }
  }, [user]);

  React.useEffect(() => { load(); }, [load]);

  const doTransfer = async () => {
    const avail = (d && d.available_pi) || 0;
    if (avail <= 0) { alert('Nothing available to transfer.'); return; }
    const amountStr = window.prompt(`How much Pi to transfer? (available: ${avail} Pi)`, String(avail));
    if (amountStr === null) return;
    const amount = parseFloat(amountStr);
    if (!amount || amount <= 0) { alert('Invalid amount.'); return; }
    const wallet = window.prompt('Your Pi wallet address (optional)') || '';
    const note = window.prompt('Note (optional)') || '';
    if (!window.confirm(`Record a transfer of ${amount} Pi?\n\nThis only creates the record - you still send the Pi yourself.`)) return;

    setBusy(true);
    try {
      const r = await fetch(`${API}/platform-earnings/transfer/`, {
        method: 'POST', headers: authHeaders(user),
        body: JSON.stringify({ amount_pi: amount, pi_wallet_address: wallet, note }),
      });
      const res = await r.json();
      if (!r.ok) throw new Error(res.error || 'Failed');
      alert(`Recorded. Remaining available: ${res.available_after_pi} Pi`);
      load();
    } catch (e) { alert(e.message); }
    setBusy(false);
  };

  const markSent = async (id) => {
    const tx = window.prompt('Pi transaction ID (optional)') || '';
    if (!window.confirm('Mark this transfer as completed?')) return;
    setBusy(true);
    try {
      const r = await fetch(`${API}/platform-earnings/transfer/${id}/complete/`, {
        method: 'POST', headers: authHeaders(user),
        body: JSON.stringify({ tx_id: tx }),
      });
      const res = await r.json();
      if (!r.ok) throw new Error(res.error || 'Failed');
      load();
    } catch (e) { alert(e.message); }
    setBusy(false);
  };

  const box = { flex: 1, minWidth: 95, background: '#F4F3EE', borderRadius: 8, padding: '8px 10px' };

  return (
    <div style={{ background: '#fff', border: '1px solid #E3E1D9', borderRadius: 12, padding: '1rem', marginBottom: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <h3 style={{ fontSize: 15, margin: 0 }}>💰 Platform Earnings</h3>
        <button onClick={load} style={{ fontSize: 12, padding: '4px 10px', border: '1px solid #E3E1D9', borderRadius: 6, background: '#fff', cursor: 'pointer' }}>🔄</button>
      </div>

      {!d ? (
        <p style={{ fontSize: 13, color: '#5F5E5A' }}>Loading…</p>
      ) : d.error ? (
        <p style={{ fontSize: 13, color: '#C0392B' }}>{d.error}</p>
      ) : (
        <>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
            <div style={box}>
              <p style={{ fontSize: 11, color: '#5F5E5A', margin: 0 }}>Available</p>
              <p style={{ fontSize: 17, fontWeight: 700, margin: 0, color: '#0F6E56' }}>{d.available_pi} Pi</p>
            </div>
            <div style={box}>
              <p style={{ fontSize: 11, color: '#5F5E5A', margin: 0 }}>Realized total</p>
              <p style={{ fontSize: 17, fontWeight: 700, margin: 0 }}>{d.realized_total_pi} Pi</p>
            </div>
            <div style={box}>
              <p style={{ fontSize: 11, color: '#5F5E5A', margin: 0 }}>Transferred</p>
              <p style={{ fontSize: 17, fontWeight: 700, margin: 0 }}>{d.transferred_pi} Pi</p>
            </div>
            {d.pending_pi > 0 && (
              <div style={box}>
                <p style={{ fontSize: 11, color: '#5F5E5A', margin: 0 }}>Pending</p>
                <p style={{ fontSize: 17, fontWeight: 700, margin: 0, color: '#91600C' }}>{d.pending_pi} Pi</p>
              </div>
            )}
          </div>

          <button
            onClick={doTransfer}
            disabled={busy || !d.available_pi}
            style={{ width: '100%', padding: '9px', fontSize: 13, fontWeight: 600, border: 'none', borderRadius: 8, background: d.available_pi ? '#0F6E56' : '#B4B2A9', color: '#fff', cursor: d.available_pi ? 'pointer' : 'not-allowed', marginBottom: 10 }}
          >
            {busy ? 'Working…' : '💸 Transfer to super admin'}
          </button>

          <p style={{ fontSize: 10, color: '#5F5E5A', margin: '0 0 10px' }}>
            {d.note} Automatic Pi payouts are not available on Mainnet, so this records the transfer — you send the Pi yourself.
          </p>

          <button
            onClick={() => setOpen(!open)}
            style={{ fontSize: 12, padding: '4px 10px', border: '1px solid #E3E1D9', borderRadius: 6, background: '#fff', cursor: 'pointer', marginBottom: 8 }}
          >
            {open ? 'Hide' : 'Show'} breakdown & history
          </button>

          {open && (
            <div style={{ borderTop: '1px solid #E3E1D9', paddingTop: 10 }}>
              <p style={{ fontSize: 12, fontWeight: 600, margin: '0 0 6px' }}>Where it came from</p>
              {(d.breakdown || []).map((r) => (
                <div key={r.name} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px solid #F1EFE8' }}>
                  <span style={{ fontSize: 12 }}>{r.icon} {r.name}</span>
                  <span style={{ fontSize: 12, fontWeight: 600, color: r.amount_pi > 0 ? '#0F6E56' : '#B4B2A9' }}>{r.amount_pi} Pi</span>
                </div>
              ))}

              <p style={{ fontSize: 12, fontWeight: 600, margin: '12px 0 6px' }}>Transfer history</p>
              {(!d.history || d.history.length === 0) ? (
                <p style={{ fontSize: 12, color: '#5F5E5A' }}>No transfers yet.</p>
              ) : d.history.map((h) => (
                <div key={h.id} style={{ background: '#F7F5F0', borderRadius: 8, padding: '8px 10px', marginBottom: 6 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 13, fontWeight: 600 }}>{h.amount_pi} Pi</span>
                    <span style={{ fontSize: 11, color: h.status === 'completed' ? '#0F6E56' : '#91600C' }}>{h.status}</span>
                  </div>
                  {h.note && <p style={{ fontSize: 11, color: '#5F5E5A', margin: '2px 0' }}>{h.note}</p>}
                  {h.tx_id && <p style={{ fontSize: 10, color: '#5F5E5A', margin: '2px 0', wordBreak: 'break-all' }}>tx: {h.tx_id}</p>}
                  {h.status === 'pending' && (
                    <button onClick={() => markSent(h.id)} disabled={busy} style={{ marginTop: 4, fontSize: 11, padding: '4px 10px', border: 'none', borderRadius: 6, background: '#0F6E56', color: '#fff', cursor: 'pointer' }}>
                      ✅ Mark as sent
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
